import { Component, Input, OnDestroy } from '@angular/core';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { faBoxOpen, faChevronDown, faChevronRight, faDollyFlatbed } from '@fortawesome/free-solid-svg-icons';
import { PageContainerService } from '../../services';
import { MaterialsSchemaDto, MaterialsStageStatusDto } from '../../data/CommandServiceDtos.generated';
import { barTone, formatPercent, formatPercentPrecise, gaugeStatus, GaugeStatus } from '../project-summary/summary-ui';

@Component({
  standalone: false,
  selector: 'project-summary-materials',
  templateUrl: './project-summary-materials.component.html',
  styleUrls: ['./project-summary-materials.component.scss']
})
export class ProjectSummaryMaterialsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() materialsSummary: MaterialsStageStatusDto;
  @Input() isActive = true;
  @Input() playToken = 0;
  materialsOverallComplete: number;
  materialsAccessUrl: string;
  faMaterialLink = faDollyFlatbed;
  faBox = faBoxOpen;
  faChevronDown = faChevronDown;
  faChevronRight = faChevronRight;
  status: GaugeStatus = gaugeStatus(0);
  displayPercent = '0%';
  expandedSchemaId: number = null;
  showAllScopes = false;
  animateBars = false;
  private barTimer: any;

  constructor(private pcs: PageContainerService) {
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges) {
    this.materialsOverallComplete = this.materialsSummary?.PercentComplete || 0;
    this.materialsAccessUrl = this.materialsSummary?.MaterialsAccessUrl;
    this.status = gaugeStatus(this.materialsOverallComplete);
    this.displayPercent = formatPercent(this.materialsOverallComplete, 1);

    const schemas = this.schemas;
    if (this.expandedSchemaId == null) {
      const withPhases = schemas.find(schema => schema.MaterialsPhases && schema.MaterialsPhases.length > 0);
      this.expandedSchemaId = withPhases ? withPhases.SchemaId : (schemas[0] && schemas[0].SchemaId);
    }

    if (changes['materialsSummary'] || changes['isActive'] || changes['playToken']) {
      this.restartBarAnimation();
    }
  }

  ngOnDestroy() {
    this.clearBarTimer();
  }

  get schemas(): MaterialsSchemaDto[] {
    return this.materialsSummary?.MaterialsSchemas || [];
  }

  get visibleSchemas(): MaterialsSchemaDto[] {
    if (this.showAllScopes || this.schemas.length <= 3) {
      return this.schemas;
    }
    return this.schemas.slice(0, 3);
  }

  get scopeCount(): number {
    return this.schemas.length;
  }

  get totalItems(): number {
    return this.materialsSummary?.TotalItems || 0;
  }

  get lowestScope(): number {
    if (!this.schemas.length) {
      return 0;
    }
    return Math.min(...this.schemas.map(schema => Number(schema.PercentComplete) || 0));
  }

  get highestScope(): number {
    if (!this.schemas.length) {
      return 0;
    }
    return Math.max(...this.schemas.map(schema => Number(schema.PercentComplete) || 0));
  }

  format(value: number, decimals: number = 0): string {
    return formatPercent(value, decimals);
  }

  formatPrecise(value: number): string {
    return formatPercentPrecise(value);
  }

  tone(value: number): string {
    return barTone(value);
  }

  isExpanded(schema: MaterialsSchemaDto): boolean {
    return this.expandedSchemaId === schema.SchemaId;
  }

  toggleSchema(schema: MaterialsSchemaDto) {
    this.expandedSchemaId = this.isExpanded(schema) ? null : schema.SchemaId;
  }

  drilldownMaterials() {
    if (this.materialsAccessUrl === null) {
      this.pcs.notificationService.warning('Materials management not configured for this Project', '', { timeOut: 3000 });
      return;
    }
    window.open(this.materialsAccessUrl, '_blank');
  }

  private restartBarAnimation() {
    this.animateBars = false;
    this.clearBarTimer();
    if (!this.isActive) {
      return;
    }
    this.barTimer = setTimeout(() => {
      this.animateBars = true;
    }, 30);
  }

  private clearBarTimer() {
    if (this.barTimer) {
      clearTimeout(this.barTimer);
      this.barTimer = null;
    }
  }
}
